import React, { useMemo } from 'react';
import { useIntl } from "react-intl";
import { getPageFromList, Member, MemberPageParam } from '@/model';
import { compareDate } from '@/util/dateUtil';
import { useMembers } from '@/query/member';
import { useScenarioId, useModal, usePageParam } from "@/hook";
import Button from "@/component/Button";
import { SearchBar } from '@/component/input';
import PaginationBar from '@/component/PaginationBar';
import { AddIcon } from "@/assets/svgs/icons";

import MemberTable from "./component/MemberTable";
import MemberAddModal, { MEMBER_ADD_MODAL_ID } from "./component/MemberAddModal";
import MemberModifyModal from './component/MemberModifyModal';


const MEMBER_PAGE_SIZE = 15;

const MemberView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    const { param, setParam } = usePageParam<MemberPageParam>({
        page: 0,
        size: MEMBER_PAGE_SIZE,
        sortKey: "member_type",
        order: "asc",
    });
    
    const { data: members = [] } = useMembers(scenarioId);

    const onSort = (key: MemberPageParam["sortKey"]) => {
        if (param.sortKey === key) {
            setParam({ ...param, order: param.order === "asc" ? "desc" : "asc" });
        } else {
            setParam({ ...param, sortKey: key });
        }
    };

    const memberPage = useMemo(() => {
        const filter = (member: Member) => {
            const keyword = (param?.keyword ?? "").toLowerCase();
            if (!keyword) {
                return true;
            }
            const { username, nickname } = member.user;
            const department = member.user.department ?? "";
            return (
                username.toLowerCase().includes(keyword)
                || nickname.toLowerCase().includes(keyword)
                || department.toLowerCase().includes(keyword)
            );
        };

        const compare = (a: Member, b: Member) => {
            let diff = 0;

            if (param?.sortKey === "member_type") {
                if (a.is_admin !== b.is_admin) {
                    diff = a.is_admin ? -1 : 1;
                } else {
                    const rolesA = a.roles.map(role => role.description).join(", ");
                    const rolesB = b.roles.map(role => role.description).join(", ");
                    diff = rolesA.localeCompare(rolesB);
                }
            } else if (param?.sortKey === "department") {
                if (!a.user.department || !b.user.department) {
                    diff = a.user.department ? -1 : 1;
                } else {
                    diff = a.user.department.localeCompare(b.user.department);
                }
            }
            
            if (diff === 0 && a.updated_at && b.updated_at) {
                diff = compareDate(a.updated_at, b.updated_at);
            }
            return diff;
        };

        let sorted = [...members].filter(filter).sort(compare);
        if (param?.order === "desc") {
            sorted = sorted.reverse();
        }

        return getPageFromList(sorted, param.page, param.size);
    }, [members, param]);

    return (<>
        <MemberAddModal/>
        <MemberModifyModal/>

        <main className="scenario-content-layout"
        >
            <div className="w-md-page flex flex-col gap-3">
                <div className="search-bar-container">
                    <SearchBar width={680}
                               defaultValue={param.keyword}
                               formAriaLabel={intl.formatMessage({ id: "m09" })}
                               placeholder={intl.formatMessage({ id: "i11003" })}
                               onSearch={keyword => setParam({ ...param, keyword })}
                    />
                    <div className="search-bar-container-btn-group">
                        <Button variant="text"
                                onClick={() => modal.open(MEMBER_ADD_MODAL_ID)}
                        >
                            <AddIcon aria-hidden="true"
                                     className="size-3.5"
                            />
                            {intl.formatMessage({ id: "i11001" })}
                        </Button>
                    </div>
                </div>

                <div className="table-pagination-container">
                    <MemberTable searchKeyword={param.keyword} 
                                 members={memberPage.content}
                                 sortKey={param.sortKey!}
                                 sortOrder={param.order!}
                                 onSort={onSort}
                    />

                    <PaginationBar className="mt-2"
                                   pageData={memberPage}
                                   onClickPage={page => setParam({ ...param, page })}
                    />
                </div>
            </div>
        </main>
    </>);
};

export default MemberView;
